import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Check, Info, PlusCircle, BarChart3 } from "lucide-react";
import { ApiService } from "../../services/api";
import { useModal } from "../../hooks/useModal";

interface Question {
  id: string;
  title: string;
  description?: string;
  type: string;
  isMandatory: boolean;
  options?: any[];
  metadata?: any;
}

interface Section {
  id: string;
  title: string;
  description?: string;
  order: number;
}

interface Answer {
  questionId: string;
  answer: any;
  answerMediaId?: string;
}

function ParticipateTopNav({ onSurveysClick }: { onSurveysClick: () => void }) {
  return (
    <nav className="bg-white border-b border-gray-200 shadow-sm flex-shrink-0">
      <div className="px-6 py-3">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-900">Anketler</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-gray-600">
            <button
              type="button"
              onClick={onSurveysClick}
              className="pb-3 transition-all duration-200 text-gray-900 font-semibold border-b-2 border-gray-900">
              Tüm Anketler
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}

export default function SurveyParticipatePage() {
  const navigate = useNavigate();
  const { id: surveyId } = useParams<{ id: string }>();
  const { showAlert } = useModal();

  const shellClass = "w-full h-[calc(100vh-60px)] bg-gray-100 overflow-hidden flex flex-col";
  const scrollMainClass = "flex-1 min-h-0 overflow-y-auto overscroll-contain p-5 sm:p-6";
  const pageInnerClass = "max-w-6xl mx-auto w-full";

  const fieldClassName =
    "w-full rounded-xl border border-slate-200/90 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-800 shadow-sm transition placeholder:text-slate-400 hover:border-slate-300 hover:bg-white focus:border-blue-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-50";
  const choiceCardClass = (selected: boolean) =>
    `flex items-center gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-all text-sm ${
      selected
        ? "border-blue-300 bg-blue-50/90 ring-4 ring-blue-50 text-slate-900"
        : "border-slate-200/90 bg-white hover:border-slate-300 hover:bg-slate-50/80 text-slate-700"
    }`;

  const [survey, setSurvey] = useState<any>(null);
  const [sections, setSections] = useState<Section[]>([]);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [allQuestions, setAllQuestions] = useState<{
    [sectionId: string]: Question[];
  }>({});
  const [totalQuestionCount, setTotalQuestionCount] = useState<number>(0);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [uploadingByQuestion, setUploadingByQuestion] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const loadSurveyData = async () => {
      if (!surveyId) return;

      try {
        setIsLoading(true);

        const surveyRes = await ApiService.getSurveyPreview(surveyId);
        const normalizedSections = Array.isArray(surveyRes?.sections)
          ? [...surveyRes.sections]
              .sort((a: any, b: any) => (a?.order ?? 0) - (b?.order ?? 0))
              .map((section: any) => ({
                ...section,
                questions: Array.isArray(section?.questions)
                  ? [...section.questions].sort((a: any, b: any) => (a?.order ?? 0) - (b?.order ?? 0))
                  : [],
              }))
          : [];

        setSurvey({
          ...surveyRes,
          sections: normalizedSections,
        });
      } catch (error: any) {
        await showAlert({
          title: "Hata",
          message: "Anket yüklenemedi: " + (error.message || "Bilinmeyen hata"),
          type: "error",
        });
        navigate("/surveys");
      } finally {
        setIsLoading(false);
      }
    };

    loadSurveyData();
  }, [surveyId, navigate]);

  const goToSection = (index: number) => {
    if (index < 0 || index >= sections.length) return;
    setCurrentSectionIndex(index);
    const section = sections[index];
    setQuestions(allQuestions[section.id] || []);
  };

  const handleAnswerChange = (questionId: string, answer: any, answerMediaId?: string) => {
    setAnswers((prev) => {
      const filtered = prev.filter((a) => a.questionId !== questionId);
      return [...filtered, { questionId, answer, answerMediaId }];
    });
  };

  const getAnswer = (questionId: string) => {
    return answers.find((a) => a.questionId === questionId)?.answer || "";
  };

  const validateCurrentSection = () => {
    const usePagination = survey?.pagination === "PAGINATION";

    if (!usePagination) {
      const allMandatoryQuestions: Question[] = [];
      Object.values(allQuestions).forEach((sectionQuestions) => {
        allMandatoryQuestions.push(...sectionQuestions.filter((q) => q.isMandatory));
      });

      for (const question of allMandatoryQuestions) {
        const answer = answers.find((a) => a.questionId === question.id);
        if (
          !answer ||
          !answer.answer ||
          (Array.isArray(answer.answer) && answer.answer.length === 0) ||
          (typeof answer.answer === "string" && answer.answer.trim() === "")
        ) {
          return false;
        }
      }
      return true;
    } else {
      const mandatoryQuestions = questions.filter((q) => q.isMandatory);
      const currentAnswers = answers.filter((a) => questions.some((q) => q.id === a.questionId));

      for (const question of mandatoryQuestions) {
        const answer = currentAnswers.find((a) => a.questionId === question.id);
        if (
          !answer ||
          !answer.answer ||
          (Array.isArray(answer.answer) && answer.answer.length === 0) ||
          (typeof answer.answer === "string" && answer.answer.trim() === "")
        ) {
          return false;
        }
      }
      return true;
    }
  };

  const handleStart = async () => {
    if (!surveyId || !survey) return;

    try {
      setIsLoading(true);

      const sectionsData = survey.sections || [];
      setSections(sectionsData);

      const allQuestionsData: { [sectionId: string]: Question[] } = {};
      let totalQuestions = 0;

      for (const section of sectionsData) {
        const sectionQuestions = section.questions || [];
        allQuestionsData[section.id] = sectionQuestions;
        totalQuestions += sectionQuestions.length;
      }

      setAllQuestions(allQuestionsData);
      setTotalQuestionCount(totalQuestions);

      if (sectionsData.length > 0) {
        setCurrentSectionIndex(0);
        const firstSection = sectionsData[0];
        setQuestions(firstSection.questions || []);
      }

      setHasStarted(true);
    } catch (error: any) {
      console.error("Anket başlatılırken hata:", error);
      await showAlert({
        title: "Hata",
        message: "Anket başlatılırken hata oluştu: " + (error.message || "Bilinmeyen hata"),
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateCurrentSection()) {
      await showAlert({
        title: "Uyarı",
        message: "Lütfen tüm zorunlu soruları cevaplayın.",
        type: "warning",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const formattedAnswers = answers
        .filter((answer) => {
          if (answer.answer === null || answer.answer === undefined) return false;
          if (typeof answer.answer === 'string' && answer.answer.trim() === '') return false;
          if (Array.isArray(answer.answer) && answer.answer.length === 0) return false;
          return true;
        })
        .map((answer) => ({
          surveyQuestionId: answer.questionId,
          answer: answer.answer,
          ...(answer.answerMediaId ? { answerMediaId: answer.answerMediaId } : {}),
        }));

      if (formattedAnswers.length === 0) {
        await showAlert({
          title: 'Uyarı',
          message: 'Lütfen en az bir soruyu cevaplayın.',
          type: 'warning',
        });

        return;
      };

      await ApiService.startSurveyParticipation(surveyId!);

      await ApiService.submitSurveyAnswers(surveyId!, { answers: formattedAnswers });

      await ApiService.completeSurvey(surveyId!);

      await showAlert({
        title: "Başarılı",
        message: "Anket başarıyla gönderildi! Teşekkür ederiz.",
        type: "success",
      });
      navigate("/surveys");
    } catch (error: any) {
      await showAlert({
        title: "Hata",
        message: "Anket gönderilirken hata oluştu: " + (error.message || "Bilinmeyen hata"),
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderQuestionInput = (question: Question) => {
    const answer = getAnswer(question.id);

    switch (question.type) {
      case "TEXT":
        return (
          <textarea
            value={answer}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            className={`${fieldClassName} min-h-[120px] resize-y`}
            placeholder="Cevabınızı buraya yazın..."
          />
        );

      case "RADIO":
        return (
          <div className="space-y-2.5">
            {question.options?.map((option, index) => {
              const optionValue = option.label || option.value || option;
              const selected = answer === optionValue;
              return (
                <label key={index} className={choiceCardClass(selected)}>
                  <input
                    type="radio"
                    name={question.id}
                    value={optionValue}
                    checked={selected}
                    onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                    className="w-4 h-4 shrink-0 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <span className="font-medium">{optionValue}</span>
                </label>
              );
            })}
          </div>
        );

      case "CHECKBOX":
        return (
          <div className="space-y-2.5">
            {question.options?.map((option, index) => {
              const optionValue = option.label || option.value || option;
              const selectedOptions = Array.isArray(answer) ? answer : [];
              const selected = selectedOptions.includes(optionValue);
              return (
                <label key={index} className={choiceCardClass(selected)}>
                  <input
                    type="checkbox"
                    value={optionValue}
                    checked={selected}
                    onChange={(e) => {
                      const newSelection = e.target.checked
                        ? [...selectedOptions, optionValue]
                        : selectedOptions.filter((val) => val !== optionValue);
                      handleAnswerChange(question.id, newSelection);
                    }}
                    className="w-4 h-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="font-medium">{optionValue}</span>
                </label>
              );
            })}
          </div>
        );

      case "FILE":
        return (
          <div className="space-y-3">
            <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/80 px-4 py-8 transition hover:border-blue-300 hover:bg-blue-50/40">
              <span className="text-sm font-medium text-slate-600">Dosya seçin veya sürükleyin</span>
              <span className="mt-1 text-xs text-slate-400">En fazla 100 MB</span>
              <input
                type="file"
                className="sr-only"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file || !surveyId) return;

                  if (file.size > 100 * 1024 * 1024) {
                    await showAlert({
                      title: "Dosya Boyutu Hatası",
                      message: "Dosya boyutu en fazla 100MB olabilir.",
                      type: "warning",
                    });
                    return;
                  }

                  try {
                    setUploadingByQuestion((prev) => ({ ...prev, [question.id]: true }));
                    const uploaded = await ApiService.uploadSurveyAnswerFile(surveyId, file);
                    handleAnswerChange(
                      question.id,
                      {
                        fileName: uploaded.name,
                        url: uploaded.url,
                        mimeType: uploaded.mimeType,
                        size: uploaded.size,
                      },
                      uploaded.id
                    );
                  } catch (error: any) {
                    await showAlert({
                      title: "Yükleme Hatası",
                      message: error?.message || "Dosya yüklenirken hata oluştu.",
                      type: "error",
                    });
                  } finally {
                    setUploadingByQuestion((prev) => ({ ...prev, [question.id]: false }));
                  }
                }}
              />
            </label>
            {uploadingByQuestion[question.id] && (
              <div className="flex items-center gap-2 text-sm text-blue-600">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                Dosya yükleniyor...
              </div>
            )}
            {answer?.fileName && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                Yüklendi: <span className="font-medium">{answer.fileName}</span>
              </div>
            )}
          </div>
        );

      default:
        return (
          <input
            type="text"
            value={answer}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            className={fieldClassName}
            placeholder="Cevabınızı buraya yazın..."
          />
        );
    }
  };

  if (isLoading) {
    return (
      <div className={shellClass}>
        <ParticipateTopNav onSurveysClick={() => navigate("/surveys")} />
        <div className="flex flex-1 items-center justify-center bg-gray-100">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
        </div>
      </div>
    );
  }

  if (!survey) {
    return (
      <div className={shellClass}>
        <ParticipateTopNav onSurveysClick={() => navigate("/surveys")} />
        <div className="flex flex-1 items-center justify-center p-6 bg-gray-100">
          <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
            <h2 className="mb-2 text-xl font-semibold text-gray-900">Anket bulunamadı</h2>
            <p className="mb-6 text-gray-600">Aradığınız anket mevcut değil.</p>
            <button
              type="button"
              onClick={() => navigate("/surveys")}
              className="rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3 text-sm font-medium text-white shadow-sm transition hover:from-blue-700 hover:to-indigo-700">
              Anketler Listesine Dön
            </button>
          </div>
        </div>
      </div>
    );
  }

  const now = new Date();
  const surveyStartDate = survey?.startDate ? new Date(survey.startDate) : null;
  const surveyEndDate = survey?.endDate ? new Date(survey.endDate) : null;
  const isNotStarted = Boolean(surveyStartDate && now.getTime() < surveyStartDate.getTime());
  const isExpired = Boolean(surveyEndDate && now.getTime() > surveyEndDate.getTime());
  const cannotParticipateByDate = isNotStarted || isExpired;

  const formatSurveyDate = (value?: string | null) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString("tr-TR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!hasStarted) {
    const totalQs = survey.sections.reduce((total: number, section: any) => total + section.questions.length, 0);

    return (
      <div className={shellClass}>
        <ParticipateTopNav onSurveysClick={() => navigate("/surveys")} />

        <div className={scrollMainClass}>
          <div className={pageInnerClass}>
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="border-b border-slate-100 bg-gradient-to-br from-slate-50 via-white to-blue-50/40 px-6 py-8 sm:px-10 sm:py-10">
                <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold uppercase tracking-wider text-blue-600/90">Anket katılımı</p>
                    <h1 className="mt-2 text-2xl font-semibold leading-tight text-gray-900 sm:text-3xl">{survey.title}</h1>
                    {survey.description && (
                      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-gray-600 sm:text-base">{survey.description}</p>
                    )}
                    <div className="mt-5 h-1 w-20 rounded-full bg-blue-200" />
                  </div>
                  <div className="flex shrink-0 gap-3">
                    <div className="min-w-[100px] rounded-xl border border-slate-200/90 bg-white/90 px-5 py-4 text-center shadow-sm backdrop-blur-sm">
                      <div className="text-2xl font-semibold tabular-nums text-gray-900">{survey.sections.length}</div>
                      <div className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Bölüm</div>
                    </div>
                    <div className="min-w-[100px] rounded-xl border border-slate-200/90 bg-white/90 px-5 py-4 text-center shadow-sm backdrop-blur-sm">
                      <div className="text-2xl font-semibold tabular-nums text-gray-900">{totalQs}</div>
                      <div className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Soru</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 py-8 sm:px-10 sm:pb-10">
                <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-5 sm:p-6">
                  <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-gray-900">
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 text-blue-700">
                      <Info className="h-4 w-4" strokeWidth={2} />
                    </span>
                    Önemli bilgiler
                  </h3>
                  <ul className="space-y-3 text-sm text-gray-600">
                    <li className="flex gap-3 rounded-lg bg-white/80 px-3 py-2.5 ring-1 ring-slate-100">
                      <PlusCircle className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
                      <span>Zorunlu sorular (<span className="font-semibold text-red-500">*</span>) işaretiyle belirtilmiştir.</span>
                    </li>
                    <li className="flex gap-3 rounded-lg bg-white/80 px-3 py-2.5 ring-1 ring-slate-100">
                      <BarChart3 className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
                      <span>İlerlemenizi üst bardan veya bölüm akışından takip edebilirsiniz.</span>
                    </li>
                    <li className="flex gap-3 rounded-lg bg-white/80 px-3 py-2.5 ring-1 ring-slate-100">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                      <span>Tüm soruları yanıtladıktan sonra anketi güvenle gönderebilirsiniz.</span>
                    </li>
                  </ul>
                  <div className="mt-5 grid gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-gray-700 sm:grid-cols-2">
                    <div>
                      <span className="text-xs font-medium uppercase tracking-wide text-slate-400">Başlangıç</span>
                      <div className="font-medium text-gray-900">{formatSurveyDate(survey.startDate)}</div>
                    </div>
                    <div>
                      <span className="text-xs font-medium uppercase tracking-wide text-slate-400">Bitiş</span>
                      <div className="font-medium text-gray-900">{formatSurveyDate(survey.endDate)}</div>
                    </div>
                  </div>
                  {cannotParticipateByDate && (
                    <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-800">
                      {isExpired ? "Bu anketin süresi geçti; katılım yapılamaz." : "Bu anket henüz başlamadı."}
                    </div>
                  )}
                </div>

                <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={() => navigate("/surveys")}
                    className="rounded-lg border border-gray-300 bg-white px-6 py-3 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50">
                    Vazgeç
                  </button>
                  <button
                    type="button"
                    onClick={handleStart}
                    disabled={cannotParticipateByDate}
                    className="rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-3 text-sm font-semibold text-white shadow-md transition hover:from-blue-700 hover:to-indigo-700 disabled:cursor-not-allowed disabled:opacity-50">
                    Ankete Başla
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentSection = sections[currentSectionIndex];
  const isLastSection = currentSectionIndex === sections.length - 1;
  const canProceed = validateCurrentSection();

  const usePagination = survey?.pagination === "PAGINATION";
  const showPercentage = survey?.processType === "PERCENTAGE";

  if (!usePagination) {
    const grandTotal = Object.values(allQuestions).reduce((total, sectionQuestions) => total + sectionQuestions.length, 0);

    return (
      <div className={shellClass}>
        <ParticipateTopNav onSurveysClick={() => navigate("/surveys")} />

        <div className={scrollMainClass}>
          <div className={pageInnerClass}>
            <div className="mb-6 rounded-xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <h1 className="text-xl font-semibold text-gray-900 sm:text-2xl">{survey.title}</h1>
                  {survey.description && (
                    <p className="mt-2 line-clamp-3 text-sm text-gray-600 sm:line-clamp-none">{survey.description}</p>
                  )}
                  <div className="mt-4 h-1 w-20 rounded-full bg-blue-200" />
                </div>
                <div className="flex shrink-0 gap-3">
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-center">
                    <div className="text-lg font-semibold text-gray-900">{sections.length}</div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Bölüm</div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-center">
                    <div className="text-lg font-semibold text-gray-900">{grandTotal}</div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Soru</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6 pb-8">
              {sections.map((section, sectionIndex) => (
                <div key={section.id} className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                  <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6 sm:py-5">
                    <div className="flex items-start gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-sm font-semibold text-blue-700 shadow-sm ring-1 ring-blue-100">
                        {sectionIndex + 1}
                      </span>
                      <div className="min-w-0">
                        <h2 className="text-lg font-semibold text-gray-900 sm:text-xl">{section.title}</h2>
                        {section.description && <p className="mt-1 text-sm text-gray-600">{section.description}</p>}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6 px-5 py-6 sm:px-6 sm:py-8">
                    {allQuestions[section.id]?.length > 0 ? (
                      allQuestions[section.id].map((question, questionIndex) => (
                        <div
                          key={question.id}
                          className="rounded-xl border border-slate-100 bg-slate-50/40 p-4 sm:p-5 last:pb-5">
                          <div className="mb-4 flex items-start gap-3">
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-semibold text-blue-700">
                              {questionIndex + 1}
                            </span>
                            <div className="min-w-0 flex-1">
                              <h3 className="text-base font-medium text-gray-900">
                                {question.title}
                                {question.isMandatory && <span className="ml-1 text-red-500">*</span>}
                              </h3>
                              {question.description && (
                                <p className="mt-2 whitespace-pre-line break-words text-sm text-gray-600">{question.description}</p>
                              )}
                            </div>
                          </div>

                          <div className="sm:pl-11">{renderQuestionInput(question)}</div>
                        </div>
                      ))
                    ) : (
                      <div className="rounded-lg border border-dashed border-slate-200 py-10 text-center text-sm text-gray-500">
                        Bu bölümde soru bulunmuyor.
                      </div>
                    )}
                  </div>
                </div>
              ))}

              <div className="sticky bottom-4 z-10 rounded-xl border border-slate-200 bg-white/95 p-4 shadow-lg backdrop-blur sm:p-5">
                <div className="flex justify-center sm:justify-end">
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 px-8 py-3 text-sm font-semibold text-white shadow-md transition hover:from-emerald-700 hover:to-teal-700 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:text-base">
                    <Check className="h-5 w-5" strokeWidth={2} />
                    {isSubmitting ? "Gönderiliyor..." : "Anketi Gönder"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={shellClass}>
      <ParticipateTopNav onSurveysClick={() => navigate("/surveys")} />

      <div className={scrollMainClass}>
        <div className={pageInnerClass}>
          <div className="mb-6 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50 to-blue-50/50 px-5 py-5 sm:px-6">
              <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                <div className="min-w-0 flex-1">
                  <h1 className="text-xl font-semibold text-gray-900 sm:text-2xl">{survey.title}</h1>
                  <p className="mt-2 text-sm text-gray-600">
                    Bölüm{" "}
                    <span className="font-semibold text-gray-900">
                      {currentSectionIndex + 1} / {sections.length}
                    </span>
                    <span className="mx-2 text-slate-300">·</span>
                    <span className="text-gray-800">{currentSection?.title}</span>
                  </p>
                </div>
                <div className="flex min-w-[220px] flex-col gap-2 sm:items-end">
                  <div className="flex w-full items-center gap-3">
                    <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-200">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-300 ease-out"
                        style={{
                          width: `${((currentSectionIndex + 1) / sections.length) * 100}%`,
                        }}
                      />
                    </div>
                    <span className="w-14 shrink-0 text-right text-xs font-semibold tabular-nums text-slate-700">
                      {showPercentage
                        ? `${Math.round(((currentSectionIndex + 1) / sections.length) * 100)}%`
                        : `${currentSectionIndex + 1}/${sections.length}`}
                    </span>
                  </div>
                  <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">İlerleme</p>
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6 sm:py-5">
              <h2 className="text-lg font-semibold text-gray-900 sm:text-xl">{currentSection?.title}</h2>
              {currentSection?.description && <p className="mt-2 text-sm text-gray-600">{currentSection.description}</p>}
            </div>

            <div className="space-y-6 px-5 py-6 sm:px-6 sm:py-8">
              {questions.map((question, index) => (
                <div key={question.id} className="rounded-xl border border-slate-100 bg-slate-50/40 p-4 sm:p-5">
                  <div className="mb-4 flex items-start gap-3">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-semibold text-blue-700">
                      {index + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-base font-medium text-gray-900">
                        {question.title}
                        {question.isMandatory && <span className="ml-1 text-red-500">*</span>}
                      </h3>
                      {question.description && (
                        <p className="mt-2 whitespace-pre-line break-words text-sm text-gray-600">{question.description}</p>
                      )}
                    </div>
                  </div>

                  <div className="sm:pl-11">{renderQuestionInput(question)}</div>
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-4 border-t border-slate-100 bg-slate-50/50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <button
                type="button"
                onClick={() => goToSection(currentSectionIndex - 1)}
                disabled={currentSectionIndex === 0}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">
                <ChevronLeft className="h-4 w-4" />
                Önceki Bölüm
              </button>

              <div className="text-center text-xs text-gray-500 sm:text-sm">
                {questions.filter((q) => q.isMandatory).length > 0 && <span>* Zorunlu alanları doldurun</span>}
              </div>

              {isLastSection ? (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!canProceed || isSubmitting}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:from-emerald-700 hover:to-teal-700 disabled:cursor-not-allowed disabled:opacity-50">
                  <Check className="h-4 w-4" strokeWidth={2} />
                  {isSubmitting ? "Gönderiliyor..." : "Anketi Gönder"}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => goToSection(currentSectionIndex + 1)}
                  disabled={!canProceed}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:from-blue-700 hover:to-indigo-700 disabled:cursor-not-allowed disabled:opacity-50">
                  Sonraki Bölüm
                  <ChevronRight className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
